import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handlePayPalApproval } from '../services/paymentService';

const MembershipSuccess = () => {
  const { userProfile, fetchUserData, currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [paypalProcessing, setPaypalProcessing] = useState(false);
  const [paypalError, setPaypalError] = useState(null);

  useEffect(() => {
    const handlePayPalSuccess = async () => {
      // Check if this is a PayPal success callback
      const paypalSuccess = searchParams.get('paypal');
      const tierKey = searchParams.get('tier');
      const paymentId = searchParams.get('paymentId');
      const payerId = searchParams.get('PayerID');

      // Prevent processing if already processing or already processed
      if (paypalProcessing || paypalError) {
        return;
      }

      if (paypalSuccess === 'true' && tierKey && currentUser && paymentId) {
        setPaypalProcessing(true);
        setPaypalError(null);

        try {
          // Create mock order data for PayPal approval
          const orderData = {
            id: paymentId,
            purchase_units: [{
              amount: {
                value: tierKey === 'TIER1' ? '75.00' : tierKey === 'TIER2' ? '150.00' : '225.00',
                currency_code: 'USD'
              }
            }]
          };

          await handlePayPalApproval(orderData, currentUser.uid, tierKey);

          // Refresh user data to get updated membership status
          await fetchUserData(currentUser.uid);

          // Clear URL parameters to prevent reprocessing
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);

        } catch (error) {
          console.error('PayPal processing error:', error);
          setPaypalError(error.message);
        } finally {
          setPaypalProcessing(false);
        }
      }
    };

    if (currentUser) {
      handlePayPalSuccess();
    }
  }, [currentUser, fetchUserData, searchParams, paypalProcessing, paypalError]);

  useEffect(() => {
    // Debug: Log when we reach the success page
    console.log('MembershipSuccess page loaded');
    console.log('Current user:', currentUser?.uid);
    console.log('Search params:', Object.fromEntries(searchParams.entries()));
    console.log('Current URL:', window.location.href);

    // Refresh user data to get updated membership status (for Stripe payments)
    if (currentUser && !searchParams.get('paypal')) {
      console.log('Fetching user data for Stripe payment success');
      fetchUserData(currentUser.uid);
    }
  }, [currentUser, fetchUserData, searchParams]);

  // Prevent redirect loops by checking if we're actually on the success page
  useEffect(() => {
    if (window.location.pathname !== '/membership/success') {
      console.log('Not on success page, redirecting...');
      window.location.href = '/membership/success';
    }
  }, []);

  // Show loading state for PayPal processing
  if (paypalProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-blue-100 mb-6">
              <svg className="animate-spin h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Processing Your PayPal Payment...
            </h2>
            <p className="text-gray-600">
              Please wait while we confirm your payment and activate your membership.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state for PayPal errors
  if (paypalError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-red-100 mb-6">
              <svg className="h-12 w-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Payment Processing Error
            </h2>
            <p className="text-gray-600 mb-6">
              {paypalError}
            </p>
            <Link
              to="/register"
              className="bg-apn-blue text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-200"
            >
              Try Again
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 mb-6">
            <svg className="h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Welcome to APN!
          </h2>

          <p className="text-lg text-gray-600 mb-6">
            Your membership has been successfully activated. You now have access to all APN features and benefits.
            {searchParams.get('paypal') === 'true' && (
              <span className="block text-sm text-green-600 mt-2">
                ✓ PayPal payment completed successfully
              </span>
            )}
          </p>

          {/* Membership Details */}
          {userProfile && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership Details</h3>
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">Member:</span>
                  <span className="font-medium">{userProfile.firstName} {userProfile.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{userProfile.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium text-green-600">Active</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Member Since:</span>
                  <span className="font-medium">
                    {userProfile.memberSince ? 
                      new Date(userProfile.memberSince.seconds * 1000).toLocaleDateString() : 
                      new Date().toLocaleDateString()
                    }
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-apn-blue text-white rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">What's Next?</h3>
            <ul className="text-left space-y-2">
              <li className="flex items-center">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Complete your profile
              </li>
              <li className="flex items-center">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Join professional groups
              </li>
              <li className="flex items-center">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Browse job opportunities
              </li>
              <li className="flex items-center">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Register for upcoming events
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <Link
              to="/profile/complete"
              className="w-full bg-apn-blue text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 block text-center"
            >
              Complete Your Profile
            </Link>

            <Link
              to="/events"
              className="w-full bg-white text-apn-blue border border-apn-blue py-3 px-4 rounded-lg font-semibold hover:bg-gray-50 transition duration-200 block text-center"
            >
              Browse Events
            </Link>

            <Link
              to="/groups"
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition duration-200 block text-center"
            >
              Join Groups
            </Link>
          </div>

          {/* Support */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help getting started? 
              <Link to="/about/contact" className="text-apn-blue hover:text-blue-700 ml-1">
                Contact our support team
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipSuccess;
